import { useEffect, useState } from 'react';
import { Popover } from '@components/Popover';
import {
  FileSignature,
  ChevronDown,
  ChevronUp,
  Plus,
} from '@huspy/briks-icons';
import {
  Text,
  Button,
  Flex,
  Box,
  InputGroup,
  InputLabel,
  Input,
  InputProps,
} from '@huspy/briks-web';
import { Divider } from '@huspy/styled-system/jsx';
import { Collapse, Checkbox } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { ApplicantType } from '@modules/opportunities/entities/opportunity/opportunity.entity';
import { NOT_CATEGORIZED_DOCUMENT } from '@modules/vault/api/const';
import { VaultConfig } from '@modules/vault/api/types';
import useUpdateVault from '@modules/vault/hooks/mutations/useUpdateVault';
import { t } from 'i18next';

const baseTranslation = 'vault.config.slido.documents';

type Props = {
  vaultData: VaultConfig;
  oppId: string;
  clientId: string | undefined;
};

// this function to ensure `isRequired` not sent as part of the props
const SafeInput = (props: InputProps & { isRequired?: boolean }) => {
  const { isRequired: _, ...rest } = props;
  return <Input { ...rest } />;
};

const ApplicantDocumentsChecklist = ({
  applicant_type,
  documents,
  name,
  onChange,
  applicant_id,
  handleAddAdditionalDoc,
}: {
  applicant_type: ApplicantType;
  name: string;
  documents?: VaultConfig['applicants'][number]['required_documents'];
  applicant_id: string;
  onChange(idx: number, status: string): void;
  handleAddAdditionalDoc(name: string): void;
}) => {
  const [additionalDocName, setAdditionalDocName] = useState('');
  return (
    <Flex direction='column' gap='2' key={ applicant_type } mt='6'>
      <Text size='xl'>{name}</Text>
      <Divider mb='4' />
      <Box
        display='grid'
        gridTemplateColumns='repeat(auto-fit, minmax(200px, 1fr))'
        direction='column'
        gap='2'
      >
        {documents
          ?.filter(
            (item) => item.document_type_name !== NOT_CATEGORIZED_DOCUMENT
          )
          ?.map(({ document_type_name, status, document_type_id }, docIdx) => (
            <Flex key={ document_type_name } align='center' w='300px'>
              <Box>
                <Checkbox
                  id={ `${document_type_name}-${applicant_id}` }
                  value={ `${document_type_name}-${document_type_id}` }
                  checked={ status === 'requested' }
                  label={ document_type_name }
                  color='black'
                  radius='xl'
                  onChange={ () => {
                    onChange(
                      docIdx,
                      status === 'requested' ? 'dismissed' : 'requested'
                    );
                  } }
                />
              </Box>
            </Flex>
          ))}
        <Flex align='center' w='300px'>
          <Popover
            title={ t(`${baseTranslation}.addDocument`) }
            triggerButton={ (
              <Button
                variant='ghost'
                px='1'
                size='sm'
                data-testid='add-additional-document-btn'
              >
                <Plus />
                {t(`${baseTranslation}.addDocument`)}
              </Button>
            ) }
          >
            <Flex justify='space-between' align='end' gap='8'>
              <InputGroup w='50%'>
                <InputLabel>
                  {' '}
                  {t(`${baseTranslation}.addDocumentLabel`)}
                </InputLabel>
                <SafeInput
                  data-testid='add-additional-document-input'
                  placeholder={ t('vault.config.documentName') }
                  value={ additionalDocName }
                  onChange={ (e) => setAdditionalDocName(e.target.value) }
                />
              </InputGroup>
              <Button
                variant='primary'
                data-testid='add-additional-doc-add-btn'
                disabled={ additionalDocName.length === 0 }
                onClick={ () => {
                  handleAddAdditionalDoc(additionalDocName);
                  setAdditionalDocName('');
                } }
              >
                {t('vault.config.add')}
              </Button>
            </Flex>
          </Popover>
        </Flex>
      </Box>
    </Flex>
  );
};

const DocumentsChecklist = ({ vaultData, oppId, clientId }: Props) => {
  const { mutate: updateVault, isPending: isUpdatingVault } = useUpdateVault(
    oppId,
    clientId
  );
  const form = useForm({
    initialValues: {
      documentsPerApplicant: vaultData?.applicants.map((app) => ({
        required_documents: app.required_documents,
        applicant_id: app.applicant_id,
      })),
    },
  });
  const [opened, { toggle }] = useDisclosure(false);

  useEffect(() => {
    if (vaultData) {
      form.setValues({
        documentsPerApplicant: vaultData.applicants.map((app) => ({
          required_documents: app.required_documents,
          applicant_id: app.applicant_id,
        })),
      });
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vaultData]);

  return (
    <Flex direction='column' gap='6'>
      <Flex gap='2'>
        <Text color='text-primary' size='2xl' fontWeight='medium'>
          {t(`${baseTranslation}.title`)}
        </Text>
        <Text color='text-secondary' size='2xl'>
          {t(`${baseTranslation}.subtitle`)}
        </Text>
      </Flex>
      <Flex
        direction='column'
        borderWidth='1'
        borderStyle='solid'
        borderColor='neutral.100'
        borderRadius='4'
        p='4'
      >
        <Flex justify='space-between' align='center'>
          <Flex gap='2'>
            <FileSignature color='black' width='24' height='24' />
            <Flex direction='column'>
              <Text size='xl'>{t(`${baseTranslation}.documents`)}</Text>
              <Text
                size='md'
                color='text-secondary'
                fontStyle='italic'
                fontWeight='medium'
              >
                {t(`${baseTranslation}.description`)}
              </Text>
            </Flex>
          </Flex>
          <Button
            onClick={ toggle }
            size='xs'
            variant='subtle'
            borderRadius='50%'
          >
            {opened ? (
              <ChevronUp color='black' />
            ) : (
              <ChevronDown color='black' />
            )}
          </Button>
        </Flex>
        <Collapse in={ opened }>
          {vaultData.applicants?.map(
            (
              {
                applicant_type, first_name, last_name, applicant_id,
              },
              appIdx
            ) => (
              <ApplicantDocumentsChecklist
                applicant_id={ applicant_id }
                applicant_type={ applicant_type }
                documents={ form.values.documentsPerApplicant?.[appIdx]
                  ?.required_documents }
                name={ `${first_name ?? ''} ${last_name ?? ''}` }
                onChange={ (idx, status) => {
                  form.setFieldValue(
                    `documentsPerApplicant.${appIdx}.required_documents.${idx}.status`,
                    status
                  );
                } }
                handleAddAdditionalDoc={ (name) => {
                  form.insertListItem(
                    `documentsPerApplicant.${appIdx}.required_documents`,
                    {
                      document_type_name: name,
                      status: 'requested',
                      document_type_id: null,
                    }
                  );
                } }
                key={ applicant_id }
              />
            )
          )}
          <Flex justify='end' mt='4'>
            <Button
              variant='outline'
              data-test='save-checklist-btn'
              disabled={ !form.isDirty() || isUpdatingVault }
              onClick={ async () => {
                await updateVault({
                  selected_banks: vaultData.selected_banks,
                  applicants: form.values.documentsPerApplicant?.map((app) => ({
                    ...app,
                    required_documents: app.required_documents.filter(
                      (doc) => doc.status !== 'dismissed'
                    ),
                  })),
                });
              } }
              loading={ isUpdatingVault }
            >
              {t('vault.config.saveDocumentsChecklist')}
            </Button>
          </Flex>
        </Collapse>
      </Flex>
    </Flex>
  );
};

export default DocumentsChecklist;
